import { useCallback, useEffect, useState, type ChangeEvent } from 'react';
import {
  FormControl,
  FormHelperText,
  TextField,
  CircularProgress,
  Box,
} from '@material-ui/core';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi } from '@backstage/core-plugin-api';
import { identityApiRef } from '@backstage/core-plugin-api';

/** Pre-fills with profile email. UI from template. Use ui:options.hidden: true to auto-fill without showing the field. */
export const IdentityEmailField = ({
  onChange,
  rawErrors,
  formData,
  required,
  schema,
  uiSchema,
  readonly: readonlyProp,
}: FieldExtensionComponentProps<string>) => {
  const label = schema?.title ?? '';
  const placeholder = schema?.description ?? '';
  const readOnly = readonlyProp ?? (schema as any)?.readOnly ?? false;
  const hidden = (uiSchema as any)?.['ui:options']?.hidden === true;
  const identityApi = useApi(identityApiRef);
  const [email, setEmail] = useState<string>(formData ?? '');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const profile = await identityApi.getProfileInfo();
        const profileEmail = profile.email?.trim();
        if (!cancelled) {
          setLoading(false);
          if (profileEmail) {
            setEmail(profileEmail);
            onChange(profileEmail);
          } else if (formData) setEmail(formData);
        }
      } catch (e) {
        if (!cancelled) {
          setLoading(false);
          setLoadError(e instanceof Error ? e.message : 'Failed to load email');
          if (formData) setEmail(formData);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityApi]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value?.trim() ?? '';
      setEmail(v);
      onChange(v);
    },
    [onChange],
  );

  if (loading) {
    if (hidden) return null;
    return (
      <FormControl margin="normal" fullWidth>
        <Box display="flex" alignItems="center">
          <CircularProgress size={20} style={{ marginRight: 8 }} />
          {label ? <span>{`Loading ${label}…`}</span> : null}
        </Box>
      </FormControl>
    );
  }

  if (hidden) return null;

  const error = Boolean(rawErrors?.length && !formData) || Boolean(loadError);
  return (
    <FormControl margin="normal" required={required} error={error} fullWidth>
      <TextField
        label={label || undefined}
        placeholder={placeholder || undefined}
        value={email}
        onChange={handleChange}
        variant="outlined"
        fullWidth
        helperText={loadError ?? (placeholder || undefined)}
        inputProps={{ readOnly }}
      />
      <FormHelperText error={error}>{rawErrors?.[0]}</FormHelperText>
    </FormControl>
  );
};
